import {
  ArrowBack,
  Badge,
  Cancel,
  CheckCircle,
  Edit,
  History,
  LocalOffer,
  LocalPrintshop,
  Person,
  Print,
  Receipt,
  Restaurant,
  RestaurantMenu,
  ShoppingBag,
  TableRestaurant,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  Paper,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import moment from "moment";
import { useMemo, useState } from "react";
import { formatCurrency } from "../../utils/currency";
import DiscountDialog from "../../components/discount-dialog/DiscountDialog";
import OrderActivityDrawer from "../../components/order-activity-log-drawer/OrderActivityLogDrawer";
import { useNavigate } from "react-router-dom";

// --- MOCK DATA ---
const MOCK_ORDER = {
  id: "123",
  order_number: "ORD-2026-8842",
  status: "In Process",
  type: "Dine In",
  table: "T-04",
  area: "First Floor",
  pax: 4,
  created_at: "2026-01-17T14:30:00",
  waiter: "Rahul Sharma",
  customer: { name: "Amit Verma", phone: "+91 98765 43210" },
  kots: [
    {
      kot_number: "101",
      created_at: "2026-01-17T14:35:00",
      status: "Served",
      instructions: "Serve starters immediately",
      items: [
        {
          id: 1,
          variant_name: "Paneer Tikka",
          quantity: 1,
          price: 280,
          addons: [],
          notes: ["Less Spicy"],
        },
        {
          id: 2,
          variant_name: "Masala Papad",
          quantity: 2,
          price: 60,
          addons: [{ name: "Cheese", price: 20, quantity: 2 }],
          notes: [],
        },
      ],
    },
    {
      kot_number: "102",
      created_at: "2026-01-17T14:55:00",
      status: "Cooking",
      items: [
        {
          id: 4,
          variant_name: "Paneer Butter Masala",
          quantity: 1,
          price: 320,
          addons: [{ name: "Extra Butter", price: 30, quantity: 1 }],
          notes: [],
        },
      ],
    },
  ],
};

export default function OrderDetailsPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0); // 0=KOT, 1=Bill
  const [openOrderActivityDrawer, setOpenOrderActivityDrawer] = useState(false);
  const [isDiscountOpen, setIsDiscountOpen] = useState(false);
  const [discount, setDiscount] = useState({ type: null, value: 0 });

  const order = MOCK_ORDER;

  // --- CALCULATIONS ---
  const { consolidatedItems, billDetails } = useMemo(() => {
    const map = new Map();
    let subtotal = 0;
    let totalQty = 0;

    order.kots.forEach((kot) => {
      kot.items.forEach((item) => {
        const itemTotal = item.price * item.quantity;
        let addonTotal = 0;
        if (item.addons)
          item.addons.forEach(
            (a) => (addonTotal += a.price * (a.quantity || 1)),
          );

        subtotal += itemTotal + addonTotal;
        totalQty += item.quantity;

        const key = item.variant_name;
        if (!map.has(key))
          map.set(key, {
            ...item,
            quantity: 0,
            total_price: 0,
            all_addons: [],
          });
        const existing = map.get(key);
        existing.quantity += item.quantity;
        existing.total_price += itemTotal;
        if (item.addons.length > 0) existing.all_addons.push(...item.addons);
      });
    });

    let discountAmount = 0;
    if (discount.type === "PERCENTAGE")
      discountAmount = subtotal * (discount.value / 100);
    else if (discount.type === "FLAT") discountAmount = discount.value;

    const taxable = Math.max(0, subtotal - discountAmount);
    const tax = taxable * 0.05;

    return {
      consolidatedItems: Array.from(map.values()),
      billDetails: {
        subtotal,
        discountAmount,
        taxAmount: tax,
        grandTotal: taxable + tax,
        totalQty,
      },
    };
  }, [order, discount]);

  const handleKOTAction = (action, kotNumber) => {
    console.log(action, kotNumber);
  };

  const handleBackToOrder = () => {
    navigate(`/app/orders`);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* 1. HEADER */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton
            sx={{ bgcolor: "white", border: "1px solid #ddd" }}
            onClick={handleBackToOrder}
          >
            <ArrowBack />
          </IconButton>
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h5" fontWeight={800}>
                Order #{order.order_number.split("-").pop()}
              </Typography>
              <Chip
                label={order.type}
                variant="outlined"
                size="small"
                sx={{ fontWeight: 700 }}
              />
              <Chip
                label={order.status}
                color="warning"
                size="small"
                sx={{ fontWeight: 700 }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Created: {moment(order.created_at).format("DD MMM YYYY, hh:mm A")}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1.5}>
          <Button
            variant="outlined"
            startIcon={<Print />}
            color="inherit"
            sx={{
              bgcolor: "white",
              borderColor: "#ddd",
              color: "text.primary",
            }}
          >
            Print Bill
          </Button>
          <Button
            variant="outlined"
            startIcon={<History />}
            onClick={() => setOpenOrderActivityDrawer(true)}
            sx={{
              bgcolor: "white",
              borderColor: "#ddd",
              color: "text.primary",
            }}
          >
            Activity Log
          </Button>
          <Button variant="contained" startIcon={<Edit />}>
            Modify Order
          </Button>
        </Box>
      </Box>
      <Box
        maxWidth="1000px"
        sx={{
          margin: "0 auto",
        }}
      >
        {/* 2. CONTEXT ROW (Horizontal) */}
        <Paper
          elevation={0}
          sx={{
            p: 0,
            mb: 3,
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          {/* TABLE */}
          <Box flex={1} p={2} display="flex" alignItems="center" gap={2}>
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: "grey.50",
                color: "text.primary",
                border: "1px solid #eee",
              }}
            >
              <TableRestaurant />
            </Avatar>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                letterSpacing={1}
              >
                TABLE
              </Typography>
              <Typography variant="subtitle2" fontWeight={700}>
                {order.table} ({order.area})
              </Typography>
            </Box>
          </Box>
          <Divider orientation="vertical" flexItem />

          {/* CUSTOMER */}
          <Box flex={1} p={2} display="flex" alignItems="center" gap={2}>
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: "grey.50",
                color: "text.primary",
                border: "1px solid #eee",
              }}
            >
              <Person />
            </Avatar>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                letterSpacing={1}
              >
                CUSTOMER
              </Typography>
              <Typography variant="subtitle2" fontWeight={700}>
                {order.customer.name}
              </Typography>
              <Typography variant="caption" display="block">
                {order.customer.phone}
              </Typography>
            </Box>
          </Box>
          <Divider orientation="vertical" flexItem />

          {/* WAITER */}
          <Box flex={1} p={2} display="flex" alignItems="center" gap={2}>
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: "grey.50",
                color: "text.primary",
                border: "1px solid #eee",
              }}
            >
              <Badge />
            </Avatar>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                letterSpacing={1}
              >
                WAITER
              </Typography>
              <Typography variant="subtitle2" fontWeight={700}>
                {order.waiter}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* 3. MAIN CONTENT */}
        <Grid container spacing={3}>
          {/* LEFT COLUMN: LISTS (70%) */}
          <Grid item xs={12} md={8} flex="1">
            <Paper
              elevation={0}
              sx={{
                border: "1px solid #e0e0e0",
                overflow: "hidden",
                minHeight: "600px",
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(e, v) => setActiveTab(v)}
                sx={{ borderBottom: "1px solid #eee", bgcolor: "#fafafa" }}
              >
                <Tab
                  label="KOT Summary"
                  icon={<RestaurantMenu />}
                  iconPosition="start"
                />
                <Tab
                  label="Items Summary"
                  icon={<Receipt />}
                  iconPosition="start"
                />
              </Tabs>

              {/* === VIEW 1: KOT TIMELINE (EXACT CODE FROM DRAWER) === */}
              {activeTab === 0 && (
                <Box p={3}>
                  {order.kots.map((kot, index) => (
                    <Box key={index} sx={{ mb: 3, position: "relative" }}>
                      {/* Vertical Line */}
                      {index !== order.kots.length - 1 && (
                        <Box
                          sx={{
                            position: "absolute",
                            left: 14,
                            top: 35,
                            bottom: -20,
                            width: "2px",
                            bgcolor: "#eee",
                            zIndex: 0,
                          }}
                        />
                      )}

                      {/* Header */}
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={2}
                        mb={1}
                        position="relative"
                        zIndex={1}
                      >
                        <Avatar
                          sx={{
                            width: 30,
                            height: 30,
                            fontSize: 14,
                            bgcolor: "grey.800",
                          }}
                        >
                          {index + 1}
                        </Avatar>
                        <Box
                          flex={1}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="baseline"
                        >
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700}>
                              KOT #{kot.kot_number}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {moment(kot.created_at).format("hh:mm A")}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            color={
                              kot.status === "Cooking"
                                ? "warning.main"
                                : "success.main"
                            }
                          >
                            • {kot.status}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Card Content */}
                      <Box
                        sx={{
                          ml: 5,
                          border: "1px solid #eee",
                          borderRadius: "8px",
                          overflow: "hidden",
                        }}
                      >
                        {kot.instructions && (
                          <Box bgcolor="#fff8e1" p={1}>
                            <Typography variant="caption" color="warning.dark">
                              <b>Note:</b> {kot.instructions}
                            </Typography>
                          </Box>
                        )}

                        <List disablePadding>
                          {kot.items.map((item, i) => (
                            <ListItem
                              key={i}
                              divider={i !== kot.items.length - 1}
                              sx={{ display: "block", py: 1 }}
                            >
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="flex-start"
                              >
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>
                                    {item.quantity} x {item.variant_name}
                                  </Typography>
                                  {item.notes.map((note, n) => (
                                    <Typography
                                      key={n}
                                      variant="caption"
                                      display="block"
                                      color="error.main"
                                      fontStyle="italic"
                                      sx={{ ml: 2 }}
                                    >
                                      * {note}
                                    </Typography>
                                  ))}
                                </Box>
                                <Box textAlign="right">
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="text.secondary"
                                  >
                                    {formatCurrency(item.price)} x{" "}
                                    {item.quantity}
                                  </Typography>
                                  <Typography variant="body2" fontWeight={700}>
                                    {formatCurrency(item.price * item.quantity)}
                                  </Typography>
                                </Box>
                              </Box>
                              {item.addons.map((addon, a) => (
                                <Box
                                  key={a}
                                  display="flex"
                                  justifyContent="space-between"
                                  mt={0.5}
                                  pl={2}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    + {addon.name} ({addon.quantity} x{" "}
                                    {formatCurrency(addon.price)})
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    color="text.primary"
                                  >
                                    {formatCurrency(
                                      addon.price * (addon.quantity || 1),
                                    )}
                                  </Typography>
                                </Box>
                              ))}
                            </ListItem>
                          ))}
                        </List>

                        {/* Actions Footer */}
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          gap={1}
                          p={1}
                          bgcolor="#fafafa"
                          borderTop="1px solid #eee"
                        >
                          <Button
                            size="small"
                            color="error"
                            startIcon={<Cancel sx={{ fontSize: 16 }} />}
                            onClick={() =>
                              handleKOTAction("Cancel", kot.kot_number)
                            }
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            color="inherit"
                            startIcon={<LocalPrintshop sx={{ fontSize: 16 }} />}
                            onClick={() =>
                              handleKOTAction("Print", kot.kot_number)
                            }
                          >
                            Print
                          </Button>
                          <Button
                            size="small"
                            color="success"
                            variant="outlined"
                            startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
                            onClick={() =>
                              handleKOTAction("Ready", kot.kot_number)
                            }
                          >
                            Ready
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {/* === VIEW 2: BILL VIEW & SUMMARY (EXACT CODE FROM DRAWER) === */}
              {activeTab === 1 && (
                <Box p={3}>
                  {/* Blue Header */}
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mb={2}
                    p={1.5}
                    bgcolor="#e3f2fd"
                    borderRadius="8px"
                  >
                    <ShoppingBag color="primary" sx={{ fontSize: 20 }} />
                    <Typography
                      variant="body2"
                      color="primary.dark"
                      fontWeight={600}
                    >
                      Total Items: {billDetails.totalQty}
                    </Typography>
                  </Box>

                  <List
                    disablePadding
                    sx={{
                      border: "1px solid #eee",
                      borderRadius: "8px",
                      mb: 3,
                    }}
                  >
                    {consolidatedItems.map((item, idx) => (
                      <ListItem
                        key={idx}
                        divider={idx !== consolidatedItems.length - 1}
                        sx={{ display: "block" }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {item.quantity} x {item.variant_name}
                            </Typography>
                          </Box>
                          <Box textAlign="right">
                            <Typography variant="body2" fontWeight={700}>
                              {formatCurrency(item.total_price)}
                            </Typography>
                          </Box>
                        </Box>
                        {item.all_addons &&
                          item.all_addons.map((addon, ax) => (
                            <Box
                              key={ax}
                              display="flex"
                              justifyContent="space-between"
                              mt={0.5}
                              pl={2}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                + {addon.name} ({addon.quantity} x{" "}
                                {formatCurrency(addon.price)})
                              </Typography>
                              <Typography
                                variant="caption"
                                fontWeight={600}
                                color="text.primary"
                              >
                                {formatCurrency(
                                  addon.price * (addon.quantity || 1),
                                )}
                              </Typography>
                            </Box>
                          ))}
                      </ListItem>
                    ))}
                  </List>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                    mb={2}
                    p={1.5}
                    bgcolor="#e3f2fd"
                    borderRadius="8px"
                  >
                    <Typography
                      variant="body2"
                      color="primary.dark"
                      fontWeight={600}
                    >
                      Total Items:
                    </Typography>
                    <Typography
                      variant="body2"
                      color="primary.dark"
                      fontWeight={600}
                    >
                      {billDetails.totalQty}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* RIGHT COLUMN: PAYMENTS (30%) */}
          <Grid item xs={12} md={4} width={350}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid #e0e0e0",
                p: 3,
                position: "sticky",
                top: 20,
              }}
            >
              <Typography variant="h6" fontWeight={700} mb={3}>
                Payment Summary
              </Typography>

              <Box p={2} borderRadius="8px">
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Subtotal</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(billDetails.subtotal)}
                  </Typography>
                </Box>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">Discount</Typography>
                    {billDetails.discountAmount > 0 ? (
                      <Chip
                        label={`${discount.type === "PERCENTAGE" ? discount.value + "%" : "Flat"} Applied`}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{
                          height: 20,
                          fontSize: "10px",
                          fontWeight: 700,
                        }}
                        onDelete={() => setDiscount({ type: null, value: 0 })}
                      />
                    ) : (
                      <Typography
                        variant="caption"
                        color="primary"
                        sx={{
                          cursor: "pointer",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                        }}
                        onClick={() => setIsDiscountOpen(true)}
                      >
                        + Apply Discount
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={
                      billDetails.discountAmount > 0
                        ? "success.main"
                        : "text.primary"
                    }
                  >
                    {billDetails.discountAmount > 0
                      ? `- ${formatCurrency(billDetails.discountAmount)}`
                      : "₹0.00"}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Tax (5%)</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(billDetails.taxAmount)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6" fontWeight={700}>
                    Grand Total
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color="primary.main"
                  >
                    {formatCurrency(billDetails.grandTotal)}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                startIcon={<Receipt />}
                sx={{ py: 1.5, fontSize: "1.1rem", fontWeight: 700 }}
              >
                Settle Bill
              </Button>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* DRAWERS & DIALOGS */}
      <OrderActivityDrawer
        openOrderActivityDrawer={openOrderActivityDrawer}
        setOpenOrderActivityDrawer={setOpenOrderActivityDrawer}
      />
      <DiscountDialog
        open={isDiscountOpen}
        onClose={() => setIsDiscountOpen(false)}
        onApply={setDiscount}
      />
    </Container>
  );
}
